<template>
    <div class="space-y-6">
        <div
            v-if="configs.length"
            class="space-y-3"
        >
            <div
                v-for="(config, index) in configs"
                :key="index"
            >
                <RedesignWidgetConfigSelectExtended
                    v-if="isSelectExtended(config)"
                    :value="config"
                    horizontal
                    :required="isRequiredSelectExtended(config)"
                    :error-message="getSelectExtendedError(config)"
                    class="mb-4"
                    @update:value="onSelectExtendedUpdate"
                />
                <div
                    v-else
                    class="max-w-sm"
                >
                    <RedesignWidgetConfigEntityTree
                        v-if="isAutoComplete(config)"
                        :key="`filter-tree-${config.ParameterID}`"
                        :value="config"
                        :allow-empty-selection="allowEmptySelection"
                        class="mb-4"
                        @update:value="emitUpdate"
                    />
                    <RedesignWidgetConfigAutoComplete
                        v-if="isAutoCompleteSelect(config)"
                        :value="config"
                        :allow-empty-selection="allowEmptySelection"
                        class="mb-4"
                        @update:value="emitUpdate"
                    />

                    <RedesignWidgetConfigSelect
                        v-else-if="isMultiSelect(config)"
                        :value="config"
                        @update:value="emitUpdate"
                    />

                    <RedesignWidgetOtherFilters
                        v-else-if="isOtherFilters(config.ParameterType || -1)"
                        :value="config"
                        :parameter-type="config.ParameterType"
                        @update:value="emitUpdate"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import RedesignWidgetConfigSelectExtended
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelectExtended.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'
import RedesignWidgetConfigSelect
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import RedesignWidgetOtherFilters
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetOtherFilters.vue'
import {
    isSelectExtended,
    isAutoComplete,
    isMultiSelect,
    isOtherFilters,
    isAutoCompleteSelect
} from '@/components/RedesignComponents/Widgets/widget.helper'

import { WidgetConfig } from '@/types/dashboard'
import i18n from '@/i18n'
import RedesignWidgetConfigAutoComplete
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigAutoComplete.vue'
import { QUESTIONS_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'

const requiredSelectExtendedNames = [ QUESTIONS_PARAMETER_NAME ]

const props = withDefaults(defineProps<{
    configs: WidgetConfig[]
    allowEmptySelection?: boolean
}>(), {
    allowEmptySelection: false
})

const emit = defineEmits<{
    (e: 'update:config', value: WidgetConfig): void
}>()

const selectExtendedErrors = ref<Record<number, string>>({})

function emitUpdate (config: WidgetConfig) {
    emit('update:config', config)
}

function isRequiredSelectExtended (config: WidgetConfig): boolean {
    return requiredSelectExtendedNames.includes(config.ParameterName ?? '')
}

function validateSelectExtendedValue (config: WidgetConfig): boolean {
    const value = config.WidgetParameterValue ?? config.WidgetParameterValueJson
    return !!value && String(value).trim().length > 0
}

const requiredSelectExtendedConfigs = computed(() =>
    props.configs.filter(config => isSelectExtended(config) && isRequiredSelectExtended(config))
)

function validateSelectExtended (): boolean {
    if (!requiredSelectExtendedConfigs.value.length) {
        return true
    }
    return requiredSelectExtendedConfigs.value.every(validateSelectExtendedValue)
}

function getSelectExtendedError (config: WidgetConfig): string {
    const paramId = config.ParameterID
    if (!paramId) return ''
    return selectExtendedErrors.value[paramId] || ''
}

function showSelectExtendedError () {
    const errorMessage = i18n.t('validation.required') as string
    const newErrors: Record<number, string> = {}

    requiredSelectExtendedConfigs.value.forEach(config => {
        const paramId = config.ParameterID
        if (paramId !== undefined && paramId !== null) {
            if (!validateSelectExtendedValue(config)) {
                newErrors[paramId] = errorMessage
            }
        }
    })

    selectExtendedErrors.value = newErrors
}

function clearSelectExtendedError () {
    selectExtendedErrors.value = {}
}

function onSelectExtendedUpdate (config: WidgetConfig) {
    if (config.ParameterID && selectExtendedErrors.value[config.ParameterID]) {
        if (validateSelectExtendedValue(config)) {
            const newErrors = { ...selectExtendedErrors.value }
            delete newErrors[config.ParameterID]
            selectExtendedErrors.value = newErrors
        }
    }
    emitUpdate(config)
}

defineExpose({
    validateSelectExtended,
    showSelectExtendedError,
    clearSelectExtendedError
})
</script>
